import React, { useEffect, useMemo } from 'react';
import { Heart, MessageCircle, X } from 'lucide-react';

const playMatchSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.55);
    });
  } catch (e) {
    console.warn('Audio not available:', e);
  }
};

const CONFETTI_COLORS = ['#ec4899', '#a855f7', '#f472b6', '#c084fc', '#fbbf24', '#34d399'];

export const MatchCelebration = ({ matchName, onSendMessage, onKeepSwiping }) => {
  useEffect(() => {
    playMatchSound();
  }, []);

  const confetti = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.5,
        duration: 2.5 + Math.random() * 2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 6 + Math.random() * 8,
        rotate: Math.random() * 360
      })),
    []
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      data-testid="match-celebration-modal"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {confetti.map((c) => (
          <span
            key={c.id}
            className="absolute top-[-20px] rounded-sm confetti-piece"
            style={{
              left: `${c.left}%`,
              width: c.size,
              height: c.size * 0.6,
              backgroundColor: c.color,
              transform: `rotate(${c.rotate}deg)`,
              animationDelay: `${c.delay}s`,
              animationDuration: `${c.duration}s`
            }}
          />
        ))}
      </div>

      <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center match-pop">
        <button
          onClick={onKeepSwiping}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="match-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 animate-ping opacity-30"></div>
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center heart-beat">
            <Heart className="w-12 h-12 text-white fill-white" />
          </div>
        </div>

        <h2 className="text-4xl font-bold gradient-text mb-2">It's a Match!</h2>
        <p className="text-gray-600 mb-8">
          You and <span className="font-semibold text-gray-900">{matchName}</span> liked each other's voices
        </p>

        <div className="space-y-3">
          <button
            onClick={onSendMessage}
            className="w-full py-3.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold flex items-center justify-center space-x-2 transition-all"
            data-testid="match-send-message-btn"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Send a Message</span>
          </button>
          <button
            onClick={onKeepSwiping}
            className="w-full py-3.5 rounded-full border-2 border-gray-200 hover:border-pink-300 hover:bg-pink-50 text-gray-700 font-semibold transition-all"
            data-testid="match-keep-swiping-btn"
          >
            Keep Swiping
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatchCelebration;
